/**
 * Centralized Route Paths
 *
 * Type-safe route path constants to prevent typos and enable refactoring.
 * Use these constants instead of hardcoded strings throughout the app.
 */

export const ROUTES = {
  // Public routes
  LOGIN: "/login",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  ATTENDANCE_SHARE: (token: string) => `/marcacoes/${token}`,
  APPLY: (slugOrToken: string) => `/candidaturas/${slugOrToken}`,
  APPLY_PARAM: "/candidaturas/:publicToken",
  JOBS_LIST: "/candidaturas",
  CANDIDATURA_STATUS: "/carreiras/candidatura",

  // Root
  ROOT: "/",
  DASHBOARD: "/dashboard",

  // Tasks (cross-cutting work-management)
  TASKS: "/dashboard/tasks",

  // Centro de notificações: o sino em página inteira, com histórico e resolvidas.
  NOTIFICATIONS: "/dashboard/notifications",


  // Manual interno da plataforma (markdown em src/content/help)
  HELP: "/dashboard/help",

  // Feed da empresa (cronologia agregada)

  // App do Segurança (portaria + ocorrências)
  //
  // Os quatro separadores da barra de baixo são ROTAS, não estado local: o
  // segurança fecha a app a meio de um turno e o telemóvel devolve-o ao mesmo
  // ecrã, a pesquisa (⌘K) aponta directamente para o separador certo, e o botão
  // "voltar" do Android faz o que se espera. Com estado local nada disto era
  // possível — recarregar caía sempre no ecrã inicial.
  //
  // `/dashboard/security` é o "Hoje": é a raiz porque é onde o turno começa
  // (quem é esperado) e porque já era o destino de todos os redireccionamentos
  // (login da conta de posto, guarda de rota). Mudar a raiz obrigaria a mexer
  // nesses sítios sem ganho nenhum.
  SECURITY: "/dashboard/security",
  SECURITY_GATE: "/dashboard/security/portaria",
  SECURITY_INCIDENT: "/dashboard/security/ocorrencia",
  SECURITY_LOG: "/dashboard/security/turno",
  VISITS: "/dashboard/visits",

  // Auth
  ACCESS_DENIED: "/access-denied",

  // Clients
  CLIENTS: {
    LIST: "/dashboard/clients",
    DETAIL: "/dashboard/clients/:clientId",
    ORDERS: {
      LIST: "/dashboard/clients/:clientId/orders",
      DETAIL: "/dashboard/clients/:clientId/orders/:encomendaId",
      CREATE: "/dashboard/clients/:clientId/orders/new",
      IMPORT: "/dashboard/clients/:clientId/orders/import",
    },
    INVOICES: "/dashboard/clients/:clientId/invoices",
    ARTICLES: {
      LIST: "/dashboard/clients/:clientId/articles",
      DETAIL: "/dashboard/clients/:clientId/articles/:articleId",
    },
    FACTORY_ORDERS: {
      LIST: "/dashboard/clients/:clientId/factory-orders",
      DETAIL: "/dashboard/clients/:clientId/factory-orders/:ofId",
    },
    EXPEDITION: {
      LIST: "/dashboard/clients/:clientId/expedition",
      DETAIL: "/dashboard/clients/:clientId/expedition/:shipmentId",
    },
  },

  // Articles
  ARTICLES: {
    LIST: "/dashboard/articles",
    DETAIL: "/dashboard/articles/:articleId",
    // Endereços de antes de «produto» passar a «artigo»: marcadores e ligações antigas continuam a abrir.
    LEGACY_LIST: "/dashboard/products",
    LEGACY_DETAIL: "/dashboard/products/:articleId",
    LEGACY_CLIENT_LIST: "/dashboard/clients/:clientId/products",
    LEGACY_CLIENT_DETAIL: "/dashboard/clients/:clientId/products/:articleId",
  },

  // Suppliers
  SUPPLIERS: {
    LIST: "/dashboard/suppliers",
    DETAIL: "/dashboard/suppliers/:supplierId",
    EVALUATIONS: "/dashboard/suppliers/evaluations",
    EVALUATION_DETAIL: "/dashboard/suppliers/evaluations/:evaluationId",
    TYPES: "/dashboard/suppliers/types",
    TYPE_DETAIL: "/dashboard/suppliers/types/:typeId",
    CERTIFICATE_TEMPLATES: "/dashboard/suppliers/certificate-templates",
    SCORING_RULES: "/dashboard/suppliers/scoring-rules",
  },

  // Contacts (Global) — LEVANTAMENTO-GAP.md 3.2
  CONTACTS: "/dashboard/contacts",

  // Orders (Global)
  ORDERS: {
    LIST: "/dashboard/orders",
    CREATE: "/dashboard/orders/new",
    IMPORT_PDF: "/dashboard/orders/import-pdf",
  },

  // Factory Orders
  FACTORY_ORDERS: {
    LIST: "/dashboard/factory-orders",
    DETAIL: "/dashboard/factory-orders/:ofId",
    CREATE_FOR_STOCK: "/dashboard/factory-orders/create-for-stock",
  },

  // Mapa de Produção — a listagem própria ("Produções") foi absorvida pela
  // listagem de Ordens de Fabrico (2026-09-04): era a mesma tabela filtrada
  // por estado. Ver FACTORY_ORDERS acima.
  PRODUCTIONS: {
    MAP: "/dashboard/production-map",
  },

  PROCEDURES: {
    LIST: "/dashboard/procedures",
    EDITOR: "/dashboard/procedures/:id",
    MINE: "/dashboard/my-procedures",
  },

  // Planeamento por tarefa de máquina — módulo `ordens_fabrico`, feature `planeamento`.
  PLANNING: {
    LIST: "/dashboard/planning",
  },

  // Lotes de produção (picagem na revista) — módulo `ordens_fabrico`, feature `lotes`.
  PRODUCTION_LOTS: {
    LIST: "/dashboard/production-lots",
  },

  // Revista e qualidade dos lotes — módulo `ordens_fabrico`, feature `qualidade`.
  QUALITY: {
    LIST: "/dashboard/quality",
  },

  // Subcontratação (lavandaria, corte, confecção, tricotagem externa) — módulo `ordens_fabrico`, feature `subcontratacao`.
  SUBCONTRACTING: {
    LIST: "/dashboard/subcontracting",
  },

  // Expeditions (Global)
  // Encomendas de fio e acessórios aos fornecedores — módulo `producao`.
  MATERIAL_ORDERS: {
    LIST: "/dashboard/material-orders",
  },

  // Manutenção dos teares — módulo `producao`, sem feature.
  MAINTENANCE: {
    LIST: "/dashboard/maintenance",
  },

  EXPEDITIONS: {
    LIST: "/dashboard/expeditions",
    DETAIL: "/dashboard/expeditions/:shipmentId",
  },

  // HR Module
  HR: {
    DASHBOARD: "/dashboard/hr/dashboard",
    EMPLOYEES: "/dashboard/hr/employees",
    EMPLOYEE_DETAIL: "/dashboard/hr/employees/:userId",
    DECLARATIONS: "/dashboard/hr/declarations",
    COMMUNICATIONS: "/dashboard/hr/communications",
    DEPARTMENTS: "/dashboard/hr/departments",
    TIME_TRACKING: "/dashboard/hr/time-tracking",
    TIME_BANK: "/dashboard/hr/time-bank",
    MY_TIME_BANK: "/dashboard/hr/my-time-bank",
    BONUSES: "/dashboard/hr/bonuses",
    PRIMAVERA_RULES: "/dashboard/hr/primavera-rules",
    WORKFORCE_MAP: "/dashboard/hr/workforce-map",
    SHIFTS: "/dashboard/hr/shifts",
    SCHEDULE_CHANGE_REQUESTS: "/dashboard/hr/schedule-change-requests",
    OCCUPATIONAL_HEALTH: "/dashboard/hr/occupational-health",
    VACATION_MAP: "/dashboard/hr/vacation-map",
    VACATION_CALENDAR: "/dashboard/hr/calendar",
    CALENDAR_OVERVIEW: "/dashboard/hr/calendar/overview",
    CALENDAR_INDIVIDUAL: "/dashboard/hr/calendar/individual",
    CALENDAR_TEAM: "/dashboard/hr/calendar/team",
    CALENDAR_COMPANY: "/dashboard/hr/calendar/company",
    CALENDAR_MEETING_ROOMS: "/dashboard/hr/calendar/meeting-rooms",
    SICK_LEAVE: "/dashboard/hr/sick-leave",
    COMPANY_CALENDAR: "/dashboard/hr/company-calendar",
    KPIS: "/dashboard/hr/kpis",
    VAGAS: "/dashboard/hr/vagas",
    APPLICATION_REPLY_SETTINGS: "/dashboard/hr/vagas/resposta-automatica",
    VAGA_DETAIL: "/dashboard/hr/vagas/:vagaId",
    CANDIDATURAS: "/dashboard/hr/candidaturas",
    CANDIDATOS: "/dashboard/hr/candidatos", // kept for redirect only
    JOB_FUNCTIONS: "/dashboard/hr/job-functions",
    TALENT_POOLS: "/dashboard/hr/talent-pools",
    INTERNAL_JOBS: "/dashboard/hr/internal-jobs",
    INTERNAL_JOB_DETAIL: (openingId: string) => `/dashboard/hr/internal-jobs/${openingId}`,
    INTERNAL_JOB_DETAIL_PARAM: "/dashboard/hr/internal-jobs/:openingId",
    TALENT_BANK: "/dashboard/hr/talent-bank",
    MY_TALENT_PROFILE: "/dashboard/hr/my-talent-profile",
  },

  // Users (Legacy redirects)
  USERS: {
    LIST: "/dashboard/users",
    DETAIL: "/dashboard/users/:userId",
  },

  // Settings
  SETTINGS: {
    MAIN: "/dashboard/settings",
    ACCOUNT: "/dashboard/settings/account",
    DECLARATIONS: "/dashboard/settings/declarations",
    IMPORT_EMPLOYEES: "/dashboard/settings/import-employees",
    AUDIT_LOG: "/dashboard/settings/audit-log",
    AI_USAGE: "/dashboard/settings/ai-usage",
    COMPANY: "/dashboard/settings/empresa",
    EMAIL_ACCOUNTS: "/dashboard/settings/email-accounts",
    EMAIL_AUTO_REPLY: "/dashboard/settings/email-auto-reply",
    PERMISSIONS: "/dashboard/settings/permissions",
    PRIMAVERA_COMPARE: "/dashboard/settings/primavera-comparar",
    MEETING_ROOMS: "/dashboard/settings/salas-de-reuniao",
    MASTER_DATA: {
      UNITS: "/dashboard/settings/unidades",
      CERTIFICATES: "/dashboard/settings/certificados",
      MEASUREMENTS: "/dashboard/settings/medidas",
      COLORS: "/dashboard/settings/colors",
      PACKAGING_TYPES: "/dashboard/settings/embalagens",
      RAW_MATERIALS: {
        LIST: "/dashboard/settings/materia-prima",
        DETAIL: "/dashboard/settings/materia-prima/:id",
      },
      ACCESSORIES: {
        LIST: "/dashboard/settings/accessories",
        DETAIL: "/dashboard/settings/accessories/:id",
      },
      MACHINES: {
        LIST: "/dashboard/settings/machines",
        DETAIL: "/dashboard/settings/machines/:machineId",
      },
    },
  },

  // Other
  BARCODES: "/dashboard/barcodes",
  TRAININGS: {
    LIST: "/dashboard/hr/trainings",
    CREATE: "/dashboard/hr/trainings/new",
    DETAIL: "/dashboard/hr/trainings/:trainingId",
    MY_TRAININGS: "/dashboard/hr/my-trainings",
    PLAYER: "/dashboard/hr/trainings/:trainingId/player",
    PLAN: "/dashboard/hr/training-plan",
  },
  KNOWLEDGE_BASE: {
    LIST: "/dashboard/hr/knowledge-base",
    DETAIL: "/dashboard/hr/knowledge-base/:articleId",
    EDIT: "/dashboard/hr/knowledge-base/:articleId/edit",
  },
  ONBOARDING: "/dashboard/onboarding",
  LEADS: "/dashboard/leads",
  // O calendário pessoal de cada utilizador — núcleo, vive no topo da sidebar.
  CALENDAR: "/dashboard/calendar",
  RECEPTION: {
    OFFICE_STOCK: "/dashboard/reception/office-stock",
    CALLS: "/dashboard/reception/calls",
    MAIL: "/dashboard/reception/mail",
    PRINTERS: "/dashboard/reception/printers",
    SHIPMENTS: "/dashboard/reception/shipments",
    INK_CARTRIDGES: "/dashboard/reception/ink-cartridges",
    SUPPLIER_ORDERS: "/dashboard/reception/supplier-orders",
    OFFICE_REQUESTS: "/dashboard/reception/office-requests",
    SHIPMENT_SIMULATION: "/dashboard/reception/shipment-simulation",
    SANITARY_SERVICE: "/dashboard/reception/sanitary-service",
    /** Legado: «Excedentes» é hoje um separador; esta rota só redireciona. */
    SANITARY_EXCESS: "/dashboard/reception/sanitary-service/excess",
    SANITARY_SERVICE_EXCESS_TAB: "/dashboard/reception/sanitary-service?tab=excedentes",
  },
  // Rastreabilidade por lote — módulo `reciclados`.
  TRACEABILITY: {
    LOTS: "/dashboard/traceability/lots",
    MASS_BALANCE: "/dashboard/traceability/mass-balance",
    // Fecho da OF: lotes consumidos → lote de saída com a % de reciclado por massa → caixas.
    OF_CLOSURE: "/dashboard/traceability/of-closure",
    // Histórico por cliente: OFs, lotes de saída, certificados, pedidos e faturas.
    CLIENT_HISTORY: "/dashboard/traceability/client-history",
    REQUEST_TC: "/dashboard/traceability/request-tc",
    WAREHOUSES: "/dashboard/traceability/warehouses",
    CERTIFICATES: "/dashboard/traceability/certificates",
    CERTIFICATE_DETAIL: "/dashboard/traceability/certificates/:id",
    LICENSES: "/dashboard/traceability/licenses",
    CERTIFICATE_REQUESTS: "/dashboard/traceability/certificate-requests",
    DYEING: "/dashboard/traceability/dyeing",
    // §4.8 — regras de notificação configuráveis.
    ALERTS: "/dashboard/traceability/alerts",
    // §4.5 — repositório documental.
    DOCUMENTS: "/dashboard/traceability/documents",
    // §4.7 — ocorrências de conformidade.
    COMPLIANCE: "/dashboard/traceability/compliance",
  },
  // O criador de processos é do núcleo: cada pessoa desenha como trabalha (e pede o que lhe faz falta).
  PROCESSES: {
    LIST: "/dashboard/processes",
    DETAIL: "/dashboard/processes/:id",
  },
  EMAIL_TRIAGE: "/dashboard/email-triage",
  VACATION_REQUESTS: "/dashboard/hr/vacation-requests",

  // Financeiro — tesouraria, aprovação de compras e documentos financeiros.
  FINANCIAL: {
    PURCHASE_APPROVALS: "/dashboard/financial/purchase-approvals",
    TREASURY: "/dashboard/financial/treasury",
    DOCUMENTS: "/dashboard/financial/documents",
  },

  // Comercial Avançado — amostras técnicas, cotações e cockpit comercial.
  // Módulo próprio, fora de `comercial` (ADR-0006).
  COMERCIAL_AVANCADO: {
    SAMPLES: "/dashboard/comercial-avancado/samples",
    SAMPLE_DETAIL: "/dashboard/comercial-avancado/samples/:sampleId",
    QUOTES: "/dashboard/comercial-avancado/quotes",
    QUOTE_DETAIL: "/dashboard/comercial-avancado/quotes/:quoteId",
    COCKPIT: "/dashboard/comercial-avancado/cockpit",
  },

  // Energia / Melhoria Contínua Module
  ENERGIA: {
    DASHBOARD: "/dashboard/energia/dashboard",
    MAPA: "/dashboard/energia/mapa",
    INDICADORES: "/dashboard/energia/indicadores",
    INDICADOR_NEW: "/dashboard/energia/indicadores/new",
    INDICADOR_DETAIL: "/dashboard/energia/indicadores/:indicadorId",
    ENERGIA_ELETRICA: "/dashboard/energia/energia-eletrica",
    AGUA: "/dashboard/energia/agua",
    GAS: "/dashboard/energia/gas",
    COMBUSTIVEIS: "/dashboard/energia/combustiveis",
    RESIDUOS: "/dashboard/energia/residuos",
    AMBIENTE: "/dashboard/energia/ambiente",
    MELHORIA: "/dashboard/energia/melhoria",
    FONTES_INFO: "/dashboard/energia/fontes-info",
    VEICULOS: "/dashboard/energia/veiculos",
    MONITORIZACAO: "/dashboard/energia/monitorizacao",
    BSC_ADMIN: "/dashboard/energia/bsc-admin",
  },

  // AI Chat Assistant
  CHAT: "/dashboard/chat",

  // Automatismos pessoais (ADR-0019): um motor da plataforma, não do bot
  AUTOMATIONS: {
    MAIN: "/dashboard/automations",
    NEW: "/dashboard/automations/new",
    DETAIL: "/dashboard/automations/:automationId",
  },

  // Legacy redirects
  LEGACY: {
    MY_ACCOUNT: "/dashboard/my-account",
  },
} as const;

/** O endereço do detalhe de um automatismo. */
export const automationPath = (automationId: string): string => ROUTES.AUTOMATIONS.DETAIL.replace(":automationId", encodeURIComponent(automationId));

/** O endereço que abre um evento do calendário — o que as notificações de convite, alteração, cancelamento, resposta e lembrete usam. */
export const calendarEventPath = (eventId: string): string => `${ROUTES.CALENDAR}?evento=${encodeURIComponent(eventId)}`;

/** Abre a lista de cotações com «Nova cotação» já aberta e preenchida: de uma amostra aprovada, de um artigo ou de um cliente. */
export const newQuotePath = (from: { clientId?: string | null; sampleId?: string | null; articleId?: string | null }): string => {
  const params = new URLSearchParams({ novo: "1" });
  if (from.clientId) params.set("cliente", from.clientId);
  if (from.sampleId) params.set("amostra", from.sampleId);
  if (from.articleId) params.set("artigo", from.articleId);
  return `${ROUTES.COMERCIAL_AVANCADO.QUOTES}?${params.toString()}`;
};
